import { Button, Checkbox, clx, Heading, Select } from '@medusajs/ui';
import React, { useCallback, useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';

import type {
  CreateAddressInput,
  CustomerAddress,
  UpdateAddressInput,
} from '@lib/query/mutations/customer';
import { FormField, FormLabel } from '@modules/common/components/form-field';
import Input from '@modules/common/components/input';
import Modal from '@modules/common/components/modal';

import { useTwZip } from './use-tw-zip';

interface EditAddressModalProps {
  address: CustomerAddress | null;
  modalState: boolean;
  closeModal: () => void;
  onSubmit: (data: CreateAddressInput | UpdateAddressInput) => void;
  isPending: boolean;
  error?: string | null;
  dataTestId: string;
  title: string;
  confirmText?: string;
}

interface FormData {
  last_name: string;
  phone: string;
  company: string;
  address_1: string;
  is_default_shipping: boolean;
}

export const EditAddressModal: React.FC<EditAddressModalProps> = ({
  address,
  modalState,
  closeModal,
  onSubmit,
  isPending,
  error,
  dataTestId,
  title,
  confirmText = '確認',
}) => {
  const {
    cities,
    districts,
    setCity,
    setDistrict,
    city,
    district,
    zipCode,
    reset: resetTwZip,
  } = useTwZip({
    city: address?.city || '',
    district: address?.postal_code?.split(' ')?.[1] || '',
  });
  const isSelectOpen = useRef(false);
  const isDrugAddress = !!address?.metadata?.is_licensed_drug_stock_location;

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isValid },
  } = useForm<FormData>({
    mode: 'onBlur',
    defaultValues: {
      last_name: address?.last_name || '',
      phone: address?.phone || '',
      company: address?.company || '',
      address_1: address?.address_1 || '',
      is_default_shipping: address?.is_default_shipping || false,
    },
  });

  // Track city/district validation separately since they're managed by useTwZip
  const [cityTouched, setCityTouched] = React.useState(false);
  const [districtTouched, setDistrictTouched] = React.useState(false);

  const cityError = cityTouched && !city ? '請選擇城市/縣' : undefined;
  const districtError =
    districtTouched && !district ? '請選擇地區' : undefined;

  // Reset form values when modal opens
  useEffect(() => {
    if (modalState) {
      reset({
        last_name: address?.last_name || '',
        phone: address?.phone || '',
        company: address?.company || '',
        address_1: address?.address_1 || '',
        is_default_shipping: address?.is_default_shipping || false,
      });
      setCityTouched(false);
      setDistrictTouched(false);
    }
  }, [modalState, address, reset]);

  // Reset city/district when modal opens with a new address
  useEffect(() => {
    if (modalState && address) {
      const addressCity = address.city || '';
      const addressDistrict = address.postal_code?.split(' ')?.[1] || '';
      if (addressCity) setCity(addressCity);
      if (addressDistrict) setDistrict(addressDistrict);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState, address?.id]);

  // Check if form can be submitted (all required fields filled)
  const canSubmit = isValid && !!city && !!district;

  const onFormSubmit = useCallback(
    (data: FormData) => {
      // Mark city/district as touched for validation
      setCityTouched(true);
      setDistrictTouched(true);

      if (!city || !district) {
        return;
      }

      const submitData: CreateAddressInput | UpdateAddressInput = {
        ...(address?.id ? { address_id: address.id } : {}),
        last_name: data.last_name,
        phone: data.phone,
        company: data.company,
        city,
        postal_code: `${zipCode} ${district}`,
        address_1: data.address_1,
        country_code: 'tw',
        is_default_shipping: data.is_default_shipping,
      };

      onSubmit(submitData);
    },
    [city, district, zipCode, onSubmit, address?.id]
  );

  const handleSelectOpen = useCallback((value: boolean) => {
    setTimeout(() => {
      isSelectOpen.current = value;
    }, 50);
  }, []);

  const handleCloseModal = useCallback(() => {
    if (isSelectOpen.current) {
      return;
    }
    resetTwZip();
    reset({
      last_name: address?.last_name || '',
      phone: address?.phone || '',
      company: address?.company || '',
      address_1: address?.address_1 || '',
      is_default_shipping: address?.is_default_shipping || false,
    });
    setCityTouched(false);
    setDistrictTouched(false);
    closeModal();
  }, [resetTwZip, closeModal, address, reset]);

  return (
    <Modal
      isOpen={modalState}
      close={handleCloseModal}
      data-testid={dataTestId}
      panelClassName="max-h-[90vh] max-w-[869px] border-0 bg-white-10"
    >
      <Modal.Title>
        <Heading className="mb-4 text-xl font-bold">{title}</Heading>
      </Modal.Title>
      <form onSubmit={handleSubmit(onFormSubmit)}>
        <Modal.Body>
          <div className="flex w-full flex-col gap-y-6">
            <FormField>
              <FormLabel className="mb-1">收件人姓名*</FormLabel>
              <Input
                placeholder="請輸入收件人姓名"
                autoComplete="family-name"
                data-testid="last-name-input"
                error={errors.last_name}
                {...register('last_name', {
                  required: '請輸入收件人姓名',
                })}
              />
            </FormField>
            <FormField>
              <FormLabel className="mb-1">收件人手機號碼*</FormLabel>
              <Input
                placeholder="請輸入市話或手機號碼"
                autoComplete="phone"
                data-testid="phone-input"
                error={errors.phone}
                {...register('phone', {
                  required: '請輸入手機號碼',
                })}
              />
            </FormField>
            <FormField>
              <FormLabel className="mb-1">藥局門市名稱*</FormLabel>
              <Input
                placeholder="例如: 大樹藥局中山門市"
                autoComplete="company"
                data-testid="company-input"
                error={errors.company}
                {...register('company', {
                  required: '請輸入藥局門市名稱',
                })}
              />
            </FormField>
            <div className="grid grid-cols-4 gap-3">
              <FormField className="col-span-2">
                <FormLabel className="mb-1">收貨地址 城市/縣*</FormLabel>
                <Select
                  name="city"
                  value={city}
                  onValueChange={(value) => {
                    setCity(value);
                    setCityTouched(true);
                  }}
                  onOpenChange={(open) => {
                    handleSelectOpen(open);
                    if (!open) {
                      setCityTouched(true);
                    }
                  }}
                  disabled={isDrugAddress}
                >
                  <Select.Trigger
                    className={clx(
                      'text-sm font-normal SelectTrigger bg-white-10 min-h-[44px] pl-4',
                      cityError && 'border-red-500'
                    )}
                  >
                    <Select.Value placeholder="請填寫城市/縣" />
                  </Select.Trigger>
                  <Select.Content className="!z-[100] rounded-md">
                    {cities.map((item) => (
                      <Select.Item key={item} value={item}>
                        {item}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select>
                {cityError && (
                  <span className="mt-1 text-sm text-red-500">{cityError}</span>
                )}
              </FormField>
              <FormField className="col-span-2">
                <FormLabel className="mb-1">地區*</FormLabel>
                <Select
                  name="district"
                  value={district}
                  onValueChange={(value) => {
                    if (!value) {
                      return;
                    }
                    setDistrict(value);
                    setDistrictTouched(true);
                  }}
                  onOpenChange={(open) => {
                    handleSelectOpen(open);
                    if (!open) {
                      setDistrictTouched(true);
                    }
                  }}
                  disabled={isDrugAddress}
                >
                  <Select.Trigger
                    className={clx(
                      'text-sm font-normal SelectTrigger bg-white-10 min-h-[44px] pl-4',
                      districtError && 'border-red-500'
                    )}
                  >
                    <Select.Value placeholder="請選擇地區" />
                  </Select.Trigger>
                  <Select.Content className="!z-[100] rounded-md">
                    {districts.map((item) => (
                      <Select.Item key={item['label']} value={item['label']}>
                        <span className="mr-1">{item['value']}</span>
                        {item['label']}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select>
                {districtError && (
                  <span className="mt-1 text-sm text-red-500">
                    {districtError}
                  </span>
                )}
              </FormField>
            </div>
            <FormField
              className={clx({
                'text-ui-fg-disabled': isDrugAddress,
              })}
            >
              <FormLabel className="mb-1">詳細地址*</FormLabel>
              <Input
                placeholder="請輸入街道、巷弄、門牌、樓層"
                autoComplete="address-line1"
                data-testid="address_1-input"
                disabled={isDrugAddress}
                error={errors.address_1}
                {...register('address_1', {
                  required: '請輸入詳細地址',
                })}
              />
              {isDrugAddress && (
                <span className="text-ui-fg-subtle ml-2 text-sm">
                  此地址為藥品專用，如有任何錯誤或需更正，請洽您的業務專員或是聯繫客服。
                </span>
              )}
            </FormField>

            {!isDrugAddress && (
              <div className="mt-[-0.5rem] flex items-center space-x-2">
                <Controller
                  name="is_default_shipping"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      data-testid="is-default-shipping-checkbox"
                      checked={field.value}
                      onCheckedChange={(checked) => field.onChange(!!checked)}
                    />
                  )}
                />
                <FormLabel>設為預設地址</FormLabel>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="mt-6">
          {error && (
            <div className="text-xs font-normal leading-5 flex-auto break-words text-rose-500">
              {error}
            </div>
          )}
          <Button
            type="reset"
            variant="secondary"
            onClick={handleCloseModal}
            className="border-black-10 text-black-10 flex-none rounded-sm border border-solid px-4 py-2"
            data-testid="cancel-button"
          >
            取消
          </Button>
          <Button
            type="submit"
            className="bg-primary-50 text-white-10 flex-none rounded-sm px-4 py-2 shadow-none disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="save-button"
            disabled={!canSubmit || isPending}
            isLoading={isPending}
          >
            {confirmText}
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};
